import React from 'react'

import getConfig from 'next/config'
import NextRouter from 'next/router'

import { CmsComponentSlot, CmsHeadTags, useBloomreach } from '@grandvisionhq/bloomreach'
import { NextPage } from '@grandvisionhq/next'
import {
  AlgoliaConfigProvider,
  AlgoliaConfigProviderProps,
  fetchAlgoliaConfig,
  SearchIndex,
  SearchPage as SearchPageComponent,
} from '@grandvisionhq/search'
import { useIntl } from '@grandvisionhq/state'
import { compose } from '@grandvisionhq/utils'

import { withCms, withLayout } from '../cms'
import { INTL_DEFAULTS } from '../configuration/intl'
import { createAlgoliaConfig } from '../util/algolia'

const { publicRuntimeConfig } = getConfig()

type InitialProps = {
  algoliaConfig: AlgoliaConfigProviderProps
  queryParams: Record<string, string | string[] | undefined>
}

type Props = InitialProps

const SearchPage: NextPage<Props, InitialProps> = ({ algoliaConfig, queryParams = {} }) => {
  const { getLabel } = useIntl()
  const { siteLogos } = useBloomreach()

  const pageTitle = queryParams.q
    ? `${getLabel('search.results.title')} '${queryParams.q}'`
    : getLabel('search.pageTitle')

  const cleanedQueryParams = Object.keys(queryParams).reduce((acc, key) => {
    if (queryParams[key] !== undefined) acc[key] = queryParams[key]
    return acc
  }, {})

  return (
    <>
      <AlgoliaConfigProvider {...algoliaConfig} NextRouter={NextRouter}>
        <CmsHeadTags title={pageTitle} noIndex={true} noFollow={true} />
        <CmsComponentSlot path="main/top-container" />
        <SearchPageComponent
          pageTitle={pageTitle}
          reimbursableLogos={{
            default: siteLogos.reimbursable,
            alternative: siteLogos.reimbursableAlternative,
          }}
          algoliaConfig={publicRuntimeConfig.algolia}
          queryParams={cleanedQueryParams}
        />
        <CmsComponentSlot path="main/bottom-container" />
      </AlgoliaConfigProvider>
    </>
  )
}

SearchPage.getInitialProps = async ({ asPath = '', query }) => {
  const contentIndex: SearchIndex = publicRuntimeConfig.algolia.indexes.find(
    ({ type }) => type === 'content'
  )
  const algoliaConfig = await fetchAlgoliaConfig({
    ...createAlgoliaConfig(asPath, undefined, undefined, {
      indices: [contentIndex.name],
    }),
    locale: INTL_DEFAULTS.locale,
    ruleContexts: ['search'],
  })

  return { queryParams: query, algoliaConfig }
}

export default compose<Props, Props>(withCms(), withLayout())(SearchPage)
